import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { ClientsApiActions } from '../../../@store/clients/clients.actions';
import {
  selectClientError,
  selectCountries,
  selectCountriesError,
  selectCountriesLoading,
  selectStatesForAddress1,
  selectStatesForAddress2,
} from '../../../@store/clients/clients.selectors';
import { ClientPayload, Country } from '../../../@store/clients/clients.state.interface';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';

@Component({
  selector: 'ciwc-add-client',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  templateUrl: './add-client.component.html',
  styleUrls: ['./add-client.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddClientComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  readonly clientError = this.store.selectSignal(selectClientError);
  readonly countries = this.store.selectSignal(selectCountries);
  readonly countriesLoading = this.store.selectSignal(selectCountriesLoading);
  readonly countriesError = this.store.selectSignal(selectCountriesError);
  readonly statesForAddress1 = this.store.selectSignal(selectStatesForAddress1);
  readonly statesForAddress2 = this.store.selectSignal(selectStatesForAddress2);

  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  isCreatingClient = signal(false);
  roles = signal<SafeAny[]>([]);
  showAddress1StateSelection = signal(false);
  showAddress1StateInput = signal(false);
  showAddress2StateSelection = signal(false);
  showAddress2StateInput = signal(false);

  clientForm = this.fb.group({
    // Basic Information
    company: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    secondaryEmail: ['', [Validators.email]],
    primaryPhone: [''],
    secondaryPhone: [''],
    fax: [''],
    terms: ['', [Validators.required]], // Default value
    creditInfo: [null, [Validators.required]], // No default value

    // Address 1
    address1Type: [''],
    customerCode1: ['', [Validators.required]],
    address1Street: ['', [Validators.required]],
    address1StreetDetails: [''],
    address1City: [''],
    address1Zip: [''],
    address1Country: [null],
    address1State: [''],

    // Address 2
    address2Type: [''],
    customerCode2: [''],
    address2Street: [''],
    address2StreetDetails: [''],
    address2City: [''],
    address2Zip: [''],
    address2Country: [null],
    address2State: [''],
  });

  constructor() {
    effect(() => {
      if (this.clientError()) {
        this.isCreatingClient.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.loadCountries();
    this.listenToCountryChanges();
  }

  onSubmit(): void {
    this.isCreatingClient.set(true);

    if (this.clientForm.valid) {
      const formValue = this.clientForm.value;

      const address1CountryValue: SafeAny = this.clientForm.get('address1Country')?.value || '';
      const address2CountryValue: SafeAny = this.clientForm.get('address2Country')?.value || '';

      const payload: ClientPayload = {
        company: formValue.company || '',
        firstName: formValue.firstName || '',
        lastName: formValue.lastName || '',
        email: formValue.email || '',
        secondaryEmail: formValue.secondaryEmail || undefined,
        primaryPhone: formValue.primaryPhone || undefined,
        secondaryPhone: formValue.secondaryPhone || undefined,
        fax: formValue.fax || undefined,
        terms: formValue.terms as 'NET30' | 'COD' | 'CIA' | 'CC' | 'Other',
        creditInfo: formValue.creditInfo || false,
        address1Type: formValue.address1Type || undefined,
        customerCode1: formValue.customerCode1 || '',
        address1Street: formValue.address1Street || '',
        address1StreetDetails: formValue.address1StreetDetails || undefined,
        address1City: formValue.address1City || undefined,
        address1Zip: formValue.address1Zip || undefined,
        address1Country: address1CountryValue.name || '',
        address1State: formValue.address1State || undefined,
        address2Type: formValue.address2Type || undefined,
        customerCode2: formValue.customerCode2 || undefined,
        address2Street: formValue.address2Street || undefined,
        address2StreetDetails: formValue.address2StreetDetails || undefined,
        address2City: formValue.address2City || undefined,
        address2Zip: formValue.address2Zip || undefined,
        address2Country: address2CountryValue.name || '',
        address2State: formValue.address2State || undefined,
      };

      this.store.dispatch(ClientsApiActions.addClient({ payload }));
    }
  }

  onCancel(): void {
    this.router.navigate(['/clients']);
  }

  private loadCountries(): void {
    this.store.dispatch(ClientsApiActions.loadCountries({ payload: { limit: -1, sort: 'name' } }));
  }

  private listenToCountryChanges(): void {
    // Add value change listeners for country inputs
    this.clientForm
      .get('address1Country')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: Country | null) => {
        if (!value) {
          this.showAddress1StateSelection.set(false);
          this.showAddress1StateInput.set(false);

          return;
        }

        if (value?.hasStates) {
          this.showAddress1StateSelection.set(true);
          this.showAddress1StateInput.set(false);

          this.store.dispatch(
            ClientsApiActions.loadStatesForAddress1({
              payload: { country: value.id, limit: -1, sort: 'name' },
            }),
          );
        } else {
          this.showAddress1StateSelection.set(false);
          this.showAddress1StateInput.set(true);
        }
      });

    this.clientForm
      .get('address2Country')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: Country | null) => {
        if (!value) {
          this.showAddress2StateSelection.set(false);
          this.showAddress2StateInput.set(false);

          return;
        }

        if (value?.hasStates) {
          this.showAddress2StateSelection.set(true);
          this.showAddress2StateInput.set(false);

          this.store.dispatch(
            ClientsApiActions.loadStatesForAddress2({
              payload: { country: value.id, limit: -1, sort: 'name' },
            }),
          );
        } else {
          this.showAddress2StateSelection.set(false);
          this.showAddress2StateInput.set(true);
        }
      });
  }
}
